/*
 * CookieConsentManager — self-hosted Consent Management Platform for the public site.
 *
 * Implements the prior-consent / opt-in model required by counsel's CIPA advisory
 * (epic GuardlyApp/Guardly-Web-App#51, WS-2 #53) using vanilla-cookieconsent
 * (orestbida), MIT-licensed and self-hosted — no third-party vendor, no recurring cost.
 *
 * What it does:
 *   - Defines consent categories: "necessary" (always on, read-only) plus
 *     "analytics" and "marketing" (OFF by default; opt-in only).
 *   - Blocks non-essential tags before consent: any future non-essential script
 *     must be added as <script type="text/plain" data-category="analytics">...; the
 *     library only activates it after the user opts in. Nothing non-essential runs
 *     until then. (The site currently loads NO non-essential tags — these
 *     categories are forward-looking scaffolding so the gate already exists.)
 *   - Honors Global Privacy Control / Do Not Track: if the browser sends GPC/DNT
 *     and the user has not already chosen, we record a non-essential opt-out and
 *     do not show the banner.
 *   - Logs consent: stores a consent record locally and, if REACT_APP_CONSENT_LOG_URL
 *     is configured, POSTs it (best-effort). NOTE: a backend endpoint to persist
 *     consent is a follow-up; see #53.
 *   - Exposes window.GuardlyConsent.openPreferences() so the footer control and the
 *     Cookie Policy page button (WS-3 #54) can reopen the preferences panel.
 *
 * BUSINESS/COUNSEL TOGGLE: SHOW_BANNER controls whether the banner auto-appears on
 * first visit. Because the site currently sets only strictly-necessary storage, a
 * banner is arguably optional today; it is enabled here as the conservative posture
 * the advisory recommends. Set to false to keep the gate + settings control in place
 * without showing a banner until a non-essential tag is actually added.
 */
import { useEffect } from 'react';
import * as CookieConsent from 'vanilla-cookieconsent';
import 'vanilla-cookieconsent/dist/cookieconsent.css';

const SHOW_BANNER = true;

// Bump when the Cookie Policy materially changes, so consent can be re-collected.
const POLICY_VERSION = '2026-06';

const hasGlobalOptOut = () => {
  if (typeof navigator === 'undefined') return false;
  return (
    navigator.globalPrivacyControl === true ||
    navigator.doNotTrack === '1' ||
    window.doNotTrack === '1' ||
    navigator.msDoNotTrack === '1'
  );
};

const logConsent = (eventType) => {
  try {
    const prefs = CookieConsent.getUserPreferences();
    const record = {
      eventType,
      timestamp: new Date().toISOString(),
      policyVersion: POLICY_VERSION,
      acceptType: prefs.acceptType,
      acceptedCategories: prefs.acceptedCategories,
      rejectedCategories: prefs.rejectedCategories,
      globalOptOut: hasGlobalOptOut(),
    };
    localStorage.setItem('guardly_cookie_consent', JSON.stringify(record));

    const url = process.env.REACT_APP_CONSENT_LOG_URL;
    if (url && typeof fetch === 'function') {
      // Best-effort; never block the UI or surface errors to the visitor.
      fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(record),
        keepalive: true,
      }).catch(() => {});
    }
  } catch (e) {
    // Consent logging must never break the page.
  }
};

let initialized = false;

const CookieConsentManager = () => {
  useEffect(() => {
    if (initialized) return;
    initialized = true;

    CookieConsent.run({
      autoShow: false, // we decide whether to show, after checking GPC/DNT
      guiOptions: {
        consentModal: { layout: 'box', position: 'bottom left', flipButtons: false },
        preferencesModal: { layout: 'box' },
      },
      categories: {
        necessary: { enabled: true, readOnly: true },
        analytics: { enabled: false },
        marketing: { enabled: false },
      },
      onFirstConsent: () => logConsent('first_consent'),
      onChange: () => logConsent('change'),
      language: {
        default: 'en',
        translations: {
          en: {
            consentModal: {
              title: 'Your privacy choices',
              description:
                'GUARDLY uses only strictly necessary browser storage to run this site. ' +
                'We do not currently use analytics or advertising technologies. If we ever ' +
                'do, they stay off until you turn them on here. See our Cookie Policy for details.',
              acceptAllBtn: 'Accept all',
              acceptNecessaryBtn: 'Reject non-essential',
              showPreferencesBtn: 'Manage preferences',
              footer:
                '<a href="/privacy">Privacy Policy</a><a href="/cookies">Cookie Policy</a>',
            },
            preferencesModal: {
              title: 'Manage cookie preferences',
              acceptAllBtn: 'Accept all',
              acceptNecessaryBtn: 'Reject non-essential',
              savePreferencesBtn: 'Save preferences',
              closeIconLabel: 'Close',
              sections: [
                {
                  title: 'Your privacy choices',
                  description:
                    'Strictly necessary storage is required to operate the site and is always on. ' +
                    'Non-essential categories below are off until you opt in.',
                },
                {
                  title: 'Strictly necessary',
                  description:
                    'Required for core features such as signing in and registering your device ' +
                    'for notifications. Cannot be switched off.',
                  linkedCategory: 'necessary',
                },
                {
                  title: 'Analytics',
                  description:
                    'Would help us understand site usage. None are currently in use.',
                  linkedCategory: 'analytics',
                },
                {
                  title: 'Marketing',
                  description:
                    'Would support advertising and campaign measurement. None are currently in use.',
                  linkedCategory: 'marketing',
                },
                {
                  title: 'More information',
                  description:
                    'For questions about your choices, see our <a href="/cookies">Cookie Policy</a> ' +
                    'or contact <a href="mailto:privacy@guardly.app">privacy@guardly.app</a>.',
                },
              ],
            },
          },
        },
      },
    });

    // Expose a stable handle for the footer control and the Cookie Policy page button.
    window.GuardlyConsent = {
      openPreferences: () => CookieConsent.showPreferences(),
      showBanner: () => CookieConsent.show(true),
    };

    // Decide what to show on this visit.
    if (CookieConsent.validConsent()) {
      return; // visitor already made a choice; respect it silently
    }
    if (hasGlobalOptOut()) {
      // Honor a universal opt-out signal: record non-essential rejection, no banner.
      CookieConsent.acceptCategory(['necessary']);
      logConsent('global_opt_out');
      return;
    }
    if (SHOW_BANNER) {
      CookieConsent.show(true);
    }
  }, []);

  return null;
};

export default CookieConsentManager;
